import { CommonModule } from '@angular/common';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ToastrService } from 'ngx-toastr';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { StoriesService } from '../../features/stories/stories.service';
import { FullscreenLoaderComponent } from '../../shared/components/loader-component/loader.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import {
  Component,
  ElementRef,
  inject,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import {
  noOnlySpacesValidator,
  noOnlySpecialCharsAlphaValidator,
  noOnlySpecialCharsValidator,
  numbersOnlyValidator,
  numericValidator,
} from '../../core/helpers/validator';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import { PageAdditionConfirmationComponent } from './modal/page-addition-confirmation.component';
import { FilenameGeneratorUtil } from '../../core/utils/filename-generator';

@Component({
  selector: 'app-bedtime-story-new',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    CommonModule,
    FullscreenLoaderComponent,
    MatSlideToggleModule,
    ContentLoaderComponent,
  ],
  templateUrl: './bedtime-story-new.component.html',
  styleUrl: './bedtime-story-new.component.scss',
})
export class BedtimeStoryNewComponent {
  readonly storage = inject(Storage);
  @ViewChild('thumbfileInput') thumbfileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('imagefileInput') imagefileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('videofileInput') videofileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('audiofileInput') audiofileInput!: ElementRef<HTMLInputElement>;
  @ViewChildren('imageFileInputs') imageFileInputs!: QueryList<
    ElementRef<HTMLInputElement>
  >;

  constructor(
    private toastr: ToastrService,
    private tagsLanguageService: TagsLanguageService,
    private storiesService: StoriesService,
    private router: Router,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {
    this.form = this.fb.group({
      langId: [1, Validators.required],
      name: [
        '',
        [
          Validators.required,
          Validators.maxLength(200),
          noOnlySpacesValidator(),
          noOnlySpecialCharsValidator(),
        ],
      ],
      writtenBy: [
        '',
        [
          Validators.required,
          Validators.maxLength(60),
          noOnlySpacesValidator(),
          noOnlySpecialCharsAlphaValidator(),
        ],
      ],
      description: [
        '',
        [
          Validators.required,
          Validators.maxLength(3000),
          noOnlySpacesValidator(),
          noOnlySpecialCharsAlphaValidator(),
        ],
      ],
      moral: [
        '',
        [
          Validators.maxLength(500),
          noOnlySpacesValidator(),
          noOnlySpecialCharsAlphaValidator(),
        ],
      ],
      tags: [[]],
      sections: this.fb.array([this.createSection()]),
      audioUrl: '',
      videoUrl: '',
      point: [
        null,
        [
          Validators.required,
          Validators.pattern(/^[0-9]+$/),
          numericValidator(1, 500),
          numbersOnlyValidator(),
        ],
      ],
      sortOrder: [
        null,
        [
          Validators.required,
          Validators.pattern(/^[1-9]\d*$/), // only positive integers
          Validators.min(1),
        ],
      ],
    });
  }

  public tags: any[] = [];
  public selectedTags: any[] = [];
  public languages: any[] = [];
  public langCode: String = '';

  form!: FormGroup;
  contentId: any = null;
  translateContentId: any = null;
  loading = false;
  contentLoading: boolean = false;
  isSubmitting = false;
  thumbUrl: string | null = null;
  thumbUploading = false;
  thumbFileName: string | null = null;
  imageUrl: string | null = null;
  imageUploading = false;
  imageFileName: string | null = null;
  imageUrls: string[] = [];
  imageNameUrls: string[] = [];
  videoUrl: string | null = null;
  videoUploading = false;
  videoFileName: string | null = null;
  audioUrl: string | null = null;
  audioUploading = false;
  audioFileName: string | null = null;
  categories: any[] = [];
  audioSource: 'upload' | 'url' = 'upload';

  ngOnInit() {
    this.getAllTags(this.langCode);
    this.getLanguages();
    this.route.paramMap.subscribe((params) => {
      const url = this.router.url;
      if (url.includes('/edit/')) {
        this.contentId = params.get('id');
        this.form.patchValue({ langId: 1 });
        this.contentLoading = true;
      }
    });

    this.route.paramMap.subscribe((params) => {
      const url = this.router.url;

      if (!url.includes('/edit/')) {
        this.translateContentId = params.get('id');
        if (this.translateContentId) {
          this.getTranslationContentById(
            this.translateContentId,
            this.langCode
          );
        }
      }
    });
  }

  videoSource: 'upload' | 'url' = 'upload';
  setVideoSource(source: 'upload' | 'url') {
    if (this.videoSource !== source) {
      this.videoSource = source;

      if (source === 'url') {
        this.clearVideoUpload();
        this.form.patchValue({ videoUrl: '' });
      } else {
        this.form.patchValue({ videoUrl: '' });
      }
    }
  }

  setAudioSource(source: 'upload' | 'url') {
    if (this.audioSource !== source) {
      this.audioSource = source;

      if (source === 'url') {
        this.clearAudioUpload();
        this.form.patchValue({ audioUrl: '' });
      } else {
        this.form.patchValue({ audioUrl: '' });
      }
    }
  }

  selectedId: number = 1;
  onLanguageSelect(event: MatSelectChange): void {
    this.contentLoading = true;

    this.selectedId = event.value;

    this.langCode = event.value;
    const selectedLang = this.languages.find(
      (lang) => lang.id === this.selectedId
    );
    if (selectedLang) {
      this.langCode = selectedLang.code;
      this.getAllTags(this.langCode);
    }
  }

 getContentById(id: any, lang: any) {
  this.storiesService.getStoriesById(id, lang).subscribe({
    next: (res) => {
      const storyData = res.data;

      this.form.patchValue({
        langId: this.selectedId,
        name: storyData.translations[0]?.name || '',
        writtenBy: storyData.translations[0]?.writtenBy || '',
        description: storyData.translations[0]?.description || '',
        moral: storyData.translations[0]?.moral || '',
        point: storyData.point,
        sortOrder: storyData.sortOrder,
      });
      this.form.updateValueAndValidity();

      const selectedTagObjects = this.tags.filter((tag) =>
        storyData.tags.some((selectedTag: any) => selectedTag.id === tag.id)
      );

      this.form.get('tags')?.setValue(selectedTagObjects);

      // Handle thumbnail with utility
      this.thumbUrl = storyData.thumbURL || null;
      this.thumbFileName = storyData.thumbURL
        ? FilenameGeneratorUtil.generateRandomFilename(storyData.thumbURL)
        : null;

      // Handle video with utility
      if (storyData.videoLink?.includes('firebasestorage')) {
        this.videoUrl = storyData.videoLink;
        this.videoFileName = FilenameGeneratorUtil.generateRandomFilename(storyData.videoLink);
        this.videoSource = 'upload';
      } else {
        this.videoUrl = null;
        this.videoFileName = null;
        this.form.patchValue({ videoUrl: storyData.videoLink });
        this.videoSource = 'url';
      }

      // Handle audio with utility
      if (storyData.audioLink?.includes('firebasestorage')) {
        this.audioUrl = storyData.audioLink;
        this.audioFileName = FilenameGeneratorUtil.generateRandomFilename(storyData.audioLink);
        this.audioSource = 'upload';
      } else {
        this.audioUrl = null;
        this.audioFileName = null;
        this.form.patchValue({ audioUrl: storyData.audioLink });
        this.audioSource = 'url';
      }

      this.setPages(storyData.pages);
      this.form.updateValueAndValidity();
      this.contentLoading = false;
    },
    error: (err) => {
      this.contentLoading = false;
    },
  });
}

  getTranslationContentById(id: any, lang: any) {
    this.storiesService.getStoriesById(id, lang).subscribe({
      next: (res) => {
        const storyData = res.data;
        const selectedTagObjects = this.tags.filter((tag) =>
          storyData.tags.some((selectedTag: any) => selectedTag.id === tag.id)
        );
        this.form.get('tags')?.setValue(selectedTagObjects);

        this.thumbUrl = storyData.thumbURL || null;
        this.thumbFileName = storyData.thumbURL || null;

        this.videoUrl = storyData.videoLink || null;
        this.videoFileName = storyData.videoLink || null;

        this.audioUrl = storyData.audioLink || null;
        this.audioFileName = storyData.audioLink || null;

        this.setTranslationPages(storyData.pages);
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

 setPages(pages: any[]) {
  const sectionsArray = this.form.get('sections') as FormArray;

  while (sectionsArray.length) {
    sectionsArray.removeAt(0);
  }
  this.imageUrls = [];
  this.imageNameUrls = [];

  pages.forEach((page) => {
    const sectionGroup = this.fb.group({
      color: [page.bgColor || '#FEF3E3'],
      content: [
        page.translations[0]?.content || '',
        [
          Validators.required,
          Validators.maxLength(1500),
          noOnlySpacesValidator(),
          noOnlySpecialCharsAlphaValidator(),
        ],
      ],
      imageFileName: [page.imageUrl || ''],
    });

    sectionsArray.push(sectionGroup);
        this.imageUrls.push(page.imageUrl || '');

    // Generate random filename for display if it's a Firebase URL
    if (page.imageUrl && page.imageUrl.includes('firebasestorage')) {
      this.imageNameUrls.push(FilenameGeneratorUtil.generateRandomFilename(page.imageUrl));
    } else {
      this.imageNameUrls.push(page.imageUrl || '');
    }
  });

  sectionsArray.updateValueAndValidity();
}

  setTranslationPages(pages: any[]) {
    const sectionsArray = this.form.get('sections') as FormArray;

    while (sectionsArray.length) {
      sectionsArray.removeAt(0);
    }

    pages.forEach((page) => {
      const sectionGroup = this.fb.group({
        color: page.bgColor || '#FEF3E3',
        content: '',
        imageFileName: page.imageUrl || '',
      });
      sectionsArray.push(sectionGroup);
      this.imageUrls.push(page.imageUrl);
      this.imageNameUrls.push(page.imageUrl);
    });
  }

  createSection() {
    return this.fb.group({
      color: ['#FEF3E3'],
      content: [
        '',
        [
          Validators.required,
          Validators.maxLength(1500),
          noOnlySpacesValidator(),
          noOnlySpecialCharsAlphaValidator(),
        ],
      ],
      imageFileName: [''],
    });
  }

  get sections() {
    return this.form.get('sections') as FormArray;
  }

  blockNegativeNumbers(event: KeyboardEvent): boolean {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];
    if (allowedKeys.includes(event.key)) {
      return true;
    }

    const isNumber = /[0-9]/.test(event.key);
    const isDecimal = event.key === '.';
    const isPasteShortcut =
      (event.ctrlKey || event.metaKey) && event.key === 'v';
    return isNumber || isDecimal || isPasteShortcut;
  }

  onPaste(event: ClipboardEvent): void {
    const pastedInput = event.clipboardData?.getData('text') || '';
    if (!/^\d+$/.test(pastedInput)) {
      event.preventDefault();
      this.toastr.warning('Only numbers are allowed');
    }
  }

  addSection() {
    const currentSection = this.sections.at(this.sections.length - 1);
    const storyContent = currentSection?.get('content')?.value;

    if (!storyContent) {
      this.toastr.error(
        'Please fill in Story Content before adding a new section.'
      );
      return;
    }

    const sectionGroup = this.fb.group({
      color: ['#FEF3E3'],
      content: [
        '',
        [
          Validators.required,
          Validators.maxLength(1500),
          noOnlySpacesValidator(),
          noOnlySpecialCharsAlphaValidator(),
        ],
      ],
      imageFileName: [''],
    });

    this.sections.push(sectionGroup);
    this.imageUrls.push('');
    this.imageNameUrls.push('');
  }

  removeSection(index: number) {
    if (this.sections.length > 1) {
      this.clearImageUpload(index);
      this.sections.removeAt(index);
    }
  }

  onCancel() {
    this.router.navigate(['/stories']);
  }

  async onVideoUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/quicktime'];
    const maxSize = 300 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Choose proper format (.mp4 or .mov)');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Choose a smaller video (less than 300 MB)');
      input.value = '';
      return;
    }

    this.videoUploading = true;

    if (!navigator.onLine) {
      this.toastr.warning(
        'No internet connection. Please check your connection and try again.'
      );
      input.value = '';
      this.videoUploading = false;
      return;
    }

    const filePath = `story-video/${file.name}_${Date.now()}`;
    const fileRef = ref(this.storage, filePath);

    try {
      await uploadBytes(fileRef, file);
      if (!navigator.onLine) {
        this.toastr.warning('Upload failed. No internet connection');
        input.value = '';
        this.videoUploading = false;
        return;
      }
      this.videoUrl = await getDownloadURL(fileRef);
      this.videoFileName = file.name;
    } catch (err) {
      this.toastr.error(
        'Upload failed due to an internal error. Please try again later.'
      );
    } finally {
      this.videoUploading = false;
    }
  }

  clearVideoUpload(): void {
    this.videoFileName = null;
    this.videoUrl = null;
    if (this.videofileInput) {
      this.videofileInput.nativeElement.value = '';
    }
  }

  async onAudioUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedTypes = ['audio/mpeg', 'audio/wav'];
    const maxSize = 100 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Choose proper format (.mp3 or .wav)');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Choose a smaller audio file (less than 100 MB)');
      input.value = '';
      return;
    }

    this.audioUploading = true;

    if (!navigator.onLine) {
      this.toastr.warning(
        'No internet connection. Please check your connection and try again.'
      );
      input.value = '';
      this.audioUploading = false;
      return;
    }

    const filePath = `story-audio/${file.name}_${Date.now()}`;
    const fileRef = ref(this.storage, filePath);

    try {
      await uploadBytes(fileRef, file);
      if (!navigator.onLine) {
        this.toastr.warning('Upload failed. No internet connection');
        input.value = '';
        this.audioUploading = false;
        return;
      }
      this.audioUrl = await getDownloadURL(fileRef);
      this.audioFileName = file.name;
    } catch (err) {
      this.toastr.error(
        'Upload failed due to an internal error. Please try again later.'
      );
    } finally {
      this.audioUploading = false;
    }
  }

  clearAudioUpload(): void {
    this.audioFileName = null;
    this.audioUrl = null;
    if (this.audiofileInput) {
      this.audiofileInput.nativeElement.value = '';
    }
  }

  async onThumbUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 15 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Choose proper Image format');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Choose a smaller image file (less than 15 MB)');
      input.value = '';
      return;
    }

    this.thumbUploading = true;
    if (!navigator.onLine) {
      this.toastr.warning(
        'No internet connection. Please check your connection and try again.'
      );
      input.value = '';
      this.thumbUploading = false;
      return;
    }

    const filePath = `story-thumb/${file.name}_${Date.now()}`;
    const fileRef = ref(this.storage, filePath);

    try {
      await uploadBytes(fileRef, file);
      if (!navigator.onLine) {
        input.value = '';
        this.toastr.warning('Upload failed. No internet connection');
        this.thumbUploading = false;
        return;
      }
      this.thumbUrl = await getDownloadURL(fileRef);
      this.thumbFileName = file.name;
    } catch (err) {
      this.toastr.error(
        'Upload failed due to an internal error. Please try again later.'
      );
    } finally {
      this.thumbUploading = false;
    }
  }

  clearThumbUpload(): void {
    this.thumbFileName = null;
    this.thumbUrl = null;
    if (this.thumbfileInput) {
      this.thumbfileInput.nativeElement.value = '';
    }
  }

  async onImageUpload(event: Event, index: number): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 15 * 1024 * 1024; // Max size: 15 MB

    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Choose proper Image format');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Choose a smaller image (less than 15 MB)');
      input.value = '';
      return;
    }

    this.imageUploading = true;
    if (!navigator.onLine) {
      this.toastr.warning(
        'No internet connection. Please check your connection and try again.'
      );
      input.value = '';
      this.imageUploading = false;
      return;
    }

    const filePath = `story-images/${file.name}_${Date.now()}`;
    const fileRef = ref(this.storage, filePath);

    try {
      await uploadBytes(fileRef, file);
      if (!navigator.onLine) {
        input.value = '';
        this.toastr.warning('Upload failed. No internet connection');
        this.imageUploading = false;
        return;
      }
      const url = await getDownloadURL(fileRef);
      this.sections.at(index).get('image')?.setValue(url);

      if (this.imageUrls.length <= index) {
        this.imageUrls.length = index + 1;
      }
      this.imageUrls[index] = url;

      if (this.imageNameUrls.length <= index) {
        this.imageNameUrls.length = index + 1;
      }
      this.imageNameUrls[index] = file.name;
    } catch (err) {
      console.error('Upload failed--', err);
      this.toastr.error(
        'Upload failed due to an internal error. Please try again later.'
      );
    } finally {
      this.imageUploading = false;
    }
  }

  clearImageUpload(index: number): void {
    this.imageUrls.splice(index, 1);
    this.imageNameUrls.splice(index, 1);

    const inputElement = this.imageFileInputs.toArray()[index];
    if (inputElement) {
      inputElement.nativeElement.value = '';
    }
  }

  getAllTags(lang: any): void {
    this.tagsLanguageService.getAllTagsWithFilter(lang).subscribe({
      next: (res) => {
        if (res?.data) {
          this.tags = res.data
            .filter((tag: any) => tag.type === 'story')
            .map((tag: any) => {
              const translation = tag.tagTranslation?.[0];
              return translation ? { ...tag, name: translation.tagName } : null;
            })
            .filter((tag: any) => tag !== null);
        }
        if (this.contentId) {
          this.getContentById(this.contentId, this.langCode);
        }
      },
      error: (err) => {
        if (this.contentId) {
          this.getContentById(this.contentId, this.langCode);
        }
      },
    });
  }

  removeTag(tagToRemove: any): void {
    const currentTags = this.form.get('tags')?.value || [];
    const updatedTags = currentTags.filter(
      (tag: any) => tag.id !== tagToRemove.id
    );

    this.form.get('tags')?.setValue(updatedTags);
    this.selectedTags = updatedTags;
  }

  isTagSelected(tag: any): boolean {
    const selectedTags = this.form.get('tags')?.value || [];
    return selectedTags.some((t: any) => t.id === tag.id);
  }

  toggleTagSelection(tag: any): void {
    const control = this.form.get('tags');
    const current = control?.value || [];

    const index = current.findIndex((t: any) => t.id === tag.id);

    if (index === -1) {
      control?.setValue([...current, tag]);
    } else {
      const updated = current.filter((t: any) => t.id !== tag.id);
      control?.setValue(updated);
    }
  }

  getLanguages(): void {
    this.tagsLanguageService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  onCategoryOpened() {
    const langId = this.form.get('langId')?.value;
    if (!langId) {
      this.toastr.warning(
        'Please choose a language before choosing the category'
      );
      this.form.get('category')?.setValue(null);
    }
  }

  createContent(): void {
    if (this.form.invalid) {
      this.toastr.warning('Please fill all required fields correctly');
      return;
    }

    const categoryId = 1;
    const selectedLangId = this.form.value.langId;
    const name = this.form.value.name.trimEnd();
    const writtenBy = this.form.value.writtenBy.trimEnd();
    const description = this.form.value.description.trimEnd();
    const moral = this.form.value.moral.trimEnd();
    const tags = this.form.value.tags.map((tag: any) => tag.id);

    if (tags.length === 0) {
      this.toastr.warning('Please choose at least one tag');
      return;
    }

    const invalidSection = this.sections.controls.find((section, index) => {
      const content = section.get('content')?.value;
      const imageUrl = this.imageUrls[index];
      return !content || !imageUrl;
    });

    if (invalidSection) {
      this.toastr.warning('Please fill out the Story Contents Properly');
      return;
    }

    if (!this.thumbUrl) {
      this.toastr.warning('Please choose a thumb image');
      return;
    }

    let totalWordCount: number | undefined;
    if (selectedLangId === 1) {
      totalWordCount = this.sections.controls.reduce((totalWords, section) => {
        const content = section.get('content')?.value || '';
        return (
          totalWords +
          content.split(' ').filter((word: any) => word.length > 0).length
        );
      }, 0);
    }

    const pages = this.sections.controls.map((section, index) => ({
      pageNo: index + 1,
      imageUrl: this.imageUrls[index] || '',
      bgColor: section.get('color')?.value || '#FEF3E3',
      translations: [
        {
          langId: selectedLangId,
          content: (section.get('content')?.value || '').trimEnd(),
        },
      ],
    }));

    const translations = [
      {
        langId: selectedLangId,
        name,
        writtenBy,
        description,
        moral,
      },
    ];

    const basePayload: any = {
      categoryId,
      thumbURL: this.thumbUrl || '',
      tags,
      point: this.form.value.point,
      sortOrder: this.form.value.sortOrder,
      videoLink: this.videoUrl || this.form.value.videoUrl || '',
      audioLink: this.audioUrl || this.form.value.audioUrl || '',
      pageCount: pages.length,
      readTime: 5,
      translations,
      pages,
    };

    if (selectedLangId === 1 && totalWordCount !== undefined) {
      basePayload.wordCount = totalWordCount;
    }

    const translatePayload = {
      contentId: this.translateContentId,
      ...basePayload,
    };

    this.loading = true;
    this.isSubmitting = true;

    if (this.contentId) {
      this.storiesService.updateStory(this.contentId, basePayload).subscribe({
        next: (res) => {
          this.toastr.success(res.message);
          this.router.navigate(['/stories']);
        },
        error: () => {
          this.loading = false;
          this.isSubmitting = false;
        },
        complete: () => {
          this.loading = false;
          this.isSubmitting = false;
        },
      });
    } else if (this.translateContentId) {
      this.storiesService.translateContent(translatePayload).subscribe({
        next: (res) => {
          this.toastr.success('Translation saved successfully');
          this.router.navigate(['/stories']);
        },
        error: () => {
          this.loading = false;
          this.isSubmitting = false;
        },
        complete: () => {
          this.loading = false;
          this.isSubmitting = false;
        },
      });
    } else {
      this.storiesService.createStory(basePayload).subscribe({
        next: () => {
          this.router.navigate(['/stories']);
        },
        error: () => {
          this.loading = false;
          this.isSubmitting = false;
        },
        complete: () => {
          this.loading = false;
          this.isSubmitting = false;
        },
      });
    }
  }

  async addSectionWithConfirmation(): Promise<void> {
    const currentSection = this.sections.at(this.sections.length - 1);
    const storyContent = currentSection?.get('content')?.value;

    if (!storyContent) {
      this.toastr.error(
        'Please fill in Story Content before adding a new section.'
      );
      return;
    }

    if (this.contentId && this.languages.length > 1) {
      const shouldProceed = await this.showPageAdditionConfirmation();
      if (!shouldProceed) {
        return;
      }
    }

    this.addSection();
  }

  private async showPageAdditionConfirmation(): Promise<boolean> {
    const dialogRef = this.dialog.open(PageAdditionConfirmationComponent, {
      width: '450px',
      maxWidth: '90vw',
      panelClass: 'page-addition-dialog',
      data: {
        message: 'You are adding a new page to this story.'
      },
      disableClose: false,
      autoFocus: false
    });

    const result = await dialogRef.afterClosed().toPromise();
    return result === true;
  }

 async checkIfStoryHasTranslations(): Promise<boolean> {
  if (this.contentId) {
    try {
      return this.languages.length > 1;
    } catch (error) {
      console.error('Error checking translations:', error);
      return false;
    }
  }
  return false;
}

  resetForm(): void {
    this.form.reset();

    const sections = this.form.get('sections') as FormArray;
    while (sections.length) {
      sections.removeAt(0);
    }
    sections.push(this.createSection());

    this.imageUrls = [];
    this.imageNameUrls = [];
    this.thumbUrl = null;
    this.videoUrl = null;
    this.audioUrl = null;
    this.thumbFileName = null;
    this.videoFileName = null;
    this.audioFileName = null;
    this.imageFileName = null;
    this.selectedTags = [];
    this.form.get('tags')?.reset();

    this.router.navigate(['/stories']);
  }
}
